import React, { ReactNode, MouseEvent } from "react";
import { useState, ChangeEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { logoutWithBackend } from "../../api/loginAPI";
import { ReactComponent as PersonIcon } from "../../assets/homePage/profilee-icon.svg";
import { ReactComponent as ShoppingCartIcon } from "../../assets/homePage/cart-icon.svg";
import { ReactComponent as SearchIcon } from "../../assets/homePage/search-icon.svg";
import { products } from "../../constants/homePage/HomeProduct";
import { ReactComponent as PermIdentityOutlinedIcon } from "../../assets/homePage/drop-profile.svg";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import "./NavBar.css";
import { AuthApiService } from "../../service/authService";
import MenuIcon from "@mui/icons-material/Menu";
import MobileDrawer from "../homePage/MobileDrawer";
import ledgerIcon from "../../assets/customerNavbarIcons/ledger.svg";
import { useRole } from "../../context/RoleContext";
import { routes } from "../../routes/routes";

interface Product {
  id: number;
  name: string;
}

const Search = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  borderRadius: theme.shape.borderRadius,
  padding: "4px 8px",
  flexGrow: 1,
}));

//search product functions
const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(1),
  fontSize: "14px",
  color: "#000",
}));

const Dropdown = styled(List)(({ theme }) => ({
  position: "absolute",
  top: "calc(85%)",
  left: 115,
  width: "70%",
  backgroundColor: "#fff",
  border: "1px solid #ccc",
  borderRadius: theme.shape.borderRadius,
  zIndex: 1000,
  maxHeight: "200px",
  overflowY: "auto",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
}));

interface MobileNavbarProps {
  children?: ReactNode;
  logo?: string;
}
const MobileNavbar = ({ children, logo }: MobileNavbarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const isMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userName] = useState<string>(localStorage.getItem("Name") || "");
  const { role } = useRole();

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      const matches = products.filter((product) =>
        product.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProducts(matches);
    } else {
      setFilteredProducts([]);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`products/${productId}`);
    setSearchQuery("");
    setFilteredProducts([]);
  };

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavigateToOrders = () => {
    navigate(`${role}/my-orders`);
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateToProfile = () => {
    navigate(`${role}/customer-profile`);
    handleMenuClose();
  };

  const handleNavigateToLedger = () => {
    navigate(`${role}/ledgers`);
    handleMenuClose();
  };
  const handleCart = () => {
    navigate(`${role}/cart`);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logoutWithBackend()
      .catch((err) => console.error("Logout failed:", err))
      .finally(() => {
        AuthApiService.logout()
          .catch((err) => console.error("Logout failed:", err))
          .finally(() => {
            navigate(`/${routes.public.signIn}`, {
              replace: true,
              state: { fromApp: true },
            });
          });
      });
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  // Ensure the JSX is properly returned here
  return (
    <div>
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "20px 5px 9px 5px",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            paddingBottom: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <IconButton
              sx={{ display: "flex", color: "black" }}
              onClick={() => toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              sx={{
                display: "flex",
                alignItems: "center",
                marginRight: "20px",
              }}
            >
              <NavLink to={`${role}/home`} style={{ textDecoration: "none" }}>
                {!logo ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{ height: "28px", cursor: "pointer" }}
                  />
                ) : (
                  <img
                    src={logo}
                    alt="Logo"
                    style={{ height: "28px", cursor: "pointer" }}
                  />
                )}
              </NavLink>
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
            }}
          >
            <IconButton
              color="inherit"
              disableRipple
              onClick={handleProfileMenuOpen}
              sx={{ marginLeft: 1.5 }}
            >
              <PersonIcon style={{ height: "14px", width: "14px" }} />
              <Typography
                variant="body2"
                sx={{ marginLeft: "5px", fontWeight: 500 }}
                data-testid="user-profile-button"
              >
                {userName}
                <strong>
                  <KeyboardArrowDownOutlinedIcon
                    sx={{
                      fontSize: "inherit",
                      position: "relative",
                      top: "3px",
                    }}
                  />
                </strong>
              </Typography>
            </IconButton>

            <IconButton color="inherit" disableRipple onClick={handleCart}>
              <ShoppingCartIcon style={{ height: "14px", width: "14px" }} />
              {cartItems && cartItems.length > 0 && (
                <Typography className="cartCount">
                  {cartItems.length}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ marginLeft: "5px", fontWeight: 500 }}
              >
                Cart
              </Typography>
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ width: window.innerWidth < 600 ? "96%" : "auto" }}>
          <Search
            sx={{
              backgroundColor: "#F1F5FF",
              boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.09)",
              border: "none",
              borderRadius: "5px",
            }}
          >
            <SearchInput
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {filteredProducts.length > 0 && (
              <Dropdown>
                {filteredProducts.map((product) => (
                  <ListItem
                    key={product.id}
                    disablePadding
                    onClick={() => handleProductClick(product.id)}
                  >
                    <ListItemButton>{product.name}</ListItemButton>
                  </ListItem>
                ))}
              </Dropdown>
            )}
            <SearchIcon />
          </Search>
        </Box>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        sx={{ fontFamily: "Poppins, sans-serif" }}
      >
        <MenuItem onClick={handleNavigateToProfile} sx={{ gap: "8px" }}>
          <PermIdentityOutlinedIcon />
          Profile
        </MenuItem>
        <MenuItem onClick={handleNavigateToLedger} sx={{ gap: "8px" }}>
          <img
            src={ledgerIcon}
            alt="Ledger Icon"
            width="16px"
            height="16px"
            style={{ display: "inline-block" }}
          />
          Ledger
        </MenuItem>

        <MenuItem onClick={handleNavigateToOrders} sx={{ gap: "8px" }}>
          <ShoppingCartOutlinedIcon />
          My Orders
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ gap: "8px" }}>
          <LogoutOutlinedIcon />
          Log Out
        </MenuItem>
      </Menu>

      {/* Drawer for mobile menu */}

      <MobileDrawer drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} />

      {children}
    </div>
  );
};

export default MobileNavbar;
